import React, { useState, useEffect, useRef } from 'react';
import './ContactUsModal.css';
import { MdEmail, MdPhone, MdLocationOn } from 'react-icons/md';
import { FaIndustry } from 'react-icons/fa';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

function ContactUsModal({ open, onClose, product = '' }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    product: '',
    city: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const modalRef = useRef();

  // Autofill product field if product prop is provided and modal is opening
  useEffect(() => {
    if (open && product && form.product !== product) {
      setForm(f => ({ ...f, product }));
    }
    if (open && !product && form.product !== '') {
      setForm(f => ({ ...f, product: '' }));
    }
    // eslint-disable-next-line
  }, [open, product]);

  // Safe scroll lock for modal
  useEffect(() => {
    if (open && modalRef.current) {
      disableBodyScroll(modalRef.current, {
        allowTouchMove: (el) => modalRef.current?.contains(el) ?? false,
      });
      document.body.classList.add('scroll-locked');
      document.getElementById('root')?.classList.add('scroll-locked');
      document.querySelector('.App')?.classList.add('scroll-locked');
    } else if (modalRef.current) {
      enableBodyScroll(modalRef.current);
      document.body.classList.remove('scroll-locked');
      document.getElementById('root')?.classList.remove('scroll-locked');
      document.querySelector('.App')?.classList.remove('scroll-locked');
    }
    return () => {
      clearAllBodyScrollLocks();
      document.body.classList.remove('scroll-locked');
      document.getElementById('root')?.classList.remove('scroll-locked');
      document.querySelector('.App')?.classList.remove('scroll-locked');
    };
  }, [open]);

  if (!open) return null;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMsg('');
    setErrorMsg('');

    try {
      // Validate form data
      if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
        setErrorMsg('Please fill in all required fields (Name, Email, and Message).');
        setLoading(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        setErrorMsg('Please enter a valid email address.');
        setLoading(false);
        return;
      }

      // Try to send via backend first
      try {
        const response = await fetch('http://localhost:5000/send-enquiry', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            phone: form.phone,
            product: form.product,
            city: form.city,
            message: form.message
          })
        });

        if (response.ok) {
          await response.json();
          setSuccessMsg('Thank you! Your enquiry has been received. We will get back to you soon via email or phone.');
          setForm({ name: '', email: '', phone: '', product: '', city: '', message: '' });
          setLoading(false);
          return;
        }
      } catch (backendError) {
        console.log('Backend not available, using fallback method');
      }

      // Fallback: Create mailto link if backend is not available
      const emailSubject = `New Enquiry from ${form.name} - ${form.product || 'General Enquiry'}`;
      const emailBody = `
Name: ${form.name}
Email: ${form.email}
Phone: ${form.phone || 'Not provided'}
Product: ${form.product || 'General enquiry'}
City: ${form.city || 'Not provided'}

Message:
${form.message}

---
This enquiry was sent from the Shri Industries website contact form.
      `.trim();

      // Create mailto link for immediate email sending
      const mailtoLink = `mailto:shri.industryich@gmail.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      
      // Open email client
      window.open(mailtoLink, '_blank');
      
      // Show success message
      setSuccessMsg('Thank you! Your enquiry has been received. We will get back to you soon via email or phone.');
      setForm({ name: '', email: '', phone: '', product: '', city: '', message: '' });
      
    } catch (err) {
      console.error('❌ Enquiry submission error:', err);
      setErrorMsg('Error processing enquiry. Please contact us directly at shri.industryich@gmail.com or call +91 9322663649.');
    }
    
    setLoading(false);
  };

  return (
    <div className="contact-modal-overlay">
      <div className="contact-modal-content" ref={modalRef}>
        <button className="contact-modal-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="contact-modal-body">
          <div className="contact-modal-left">
            <h2 className="contact-modal-title">Contact us</h2>
            
            <div className="contact-modal-desc">
              At <span className="contact-modal-highlight">Shri Industry Ichalkaranji</span>, we've been manufacturing reliable food and fruit processing machinery <span className="contact-modal-highlight">since 1996</span>. With nearly 30 years of experience, we specialize in innovative, user-friendly solutions that simplify operations for businesses across India.<br /><br />
              What started as a graduate's idea to ease coconut shredding at home has evolved into a trusted brand powering food processing nationwide. We're committed to delivering quality, durability, and performance — helping industries grow with confidence.
            </div>
            <div className="contact-modal-info-list">
              <div className="contact-modal-info"><span className="contact-modal-info-icon"><MdEmail /></span><span className="contact-modal-info-text"> shri_industry@yahoo.com</span></div>
              <div className="contact-modal-info"><span className="contact-modal-info-icon"><MdPhone /></span><span className="contact-modal-info-text"> +91 9322663649</span></div>
              <div className="contact-modal-info"><span className="contact-modal-info-icon"><MdLocationOn /></span><span className="contact-modal-info-text"> 17/141 Gurukripa, Vivekanand Colony<br />Ichalkaranji Pin code: 416115</span></div>
              <div className="contact-modal-info"><span className="contact-modal-info-icon"><FaIndustry /></span><span className="contact-modal-info-text"> Food and Fruit Machines</span></div>
            </div>
          </div>
          <form className="contact-modal-form" onSubmit={handleSubmit}>
            <label>Your Name
              <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Enter your name" required />
            </label>
            <label>Your Email
              <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="Enter your email" required />
            </label>
            <label>Phone Number
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="Enter your number" required />
            </label>
            <label>Product
              <input type="text" name="product" value={form.product} onChange={handleChange} placeholder="Enter the product you are interested" />
            </label>
            <label>City
              <input type="text" name="city" value={form.city} onChange={handleChange} placeholder="Enter your location" />
            </label>
            <label>Write your message here
              <textarea name="message" value={form.message} onChange={handleChange} placeholder="Enter your message" rows={4} />
            </label>
            <button className="contact-modal-btn" type="submit" disabled={loading}>
              {loading ? (
                <span className="spinner" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8, width: 18, height: 18, border: '3px solid #fff', borderTop: '3px solid #1976ed', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
              ) : null}
              {loading ? 'Sending...' : 'Send Enquiry'}
            </button>
            {successMsg && <div className="contact-modal-success">{successMsg}</div>}
            {errorMsg && <div className="contact-modal-error">{errorMsg}</div>}
          </form>
        </div>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .contact-modal-success {
          color: #2ecc40;
          background: #eafbe7;
          border-radius: 6px;
          padding: 10px 14px;
          margin-top: 18px;
          font-size: 1.08rem;
          text-align: center;
        }
        .contact-modal-error {
          color: #e74c3c;
          background: #fbeaea;
          border-radius: 6px;
          padding: 10px 14px;
          margin-top: 18px;
          font-size: 1.08rem;
          text-align: center;
        }
      `}</style>
    </div>
  );
}

export default ContactUsModal; 